/**
 * Filter controls for list pages. They sit on the page background in one row above the content
 * they scope, so they use the default (primary) field variant, which stands out from the background.
 */
import type { Key } from "@heroui/react";
import { ListBox, SearchField, Select, ToggleButton, ToggleButtonGroup } from "@heroui/react";
import { useEffect, useState } from "react";
import { useDebounced } from "../lib/hooks";
import type { SelectOption } from "./fields";

export const ALL = "__all__";

/** Compact select with an "all" choice. `value` is undefined when nothing is filtered. */
export function FilterSelect({
  label,
  allLabel,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  allLabel: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: SelectOption[];
  className?: string;
}) {
  return (
    <Select
      aria-label={label}
      value={value ?? ALL}
      onChange={(v) => onChange(v === null || v === ALL ? undefined : String(v))}
      className={className ?? "w-full sm:w-44"}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {[{ id: ALL, label: allLabel }, ...options].map((o) => (
            <ListBox.Item key={o.id} id={o.id} textValue={o.label}>
              <div className="flex flex-col py-0.5">
                <span>{o.label}</span>
                {"hint" in o && o.hint ? <span className="text-xs text-muted">{o.hint}</span> : null}
              </div>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

/** Search box that reports its value after typing pauses, and immediately on Enter. */
export function SearchBox({
  label,
  placeholder,
  value,
  onChange,
  className,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  const [text, setText] = useState(value);
  const debounced = useDebounced(text);
  // Follow outside changes, e.g. "Clear filters" or browser navigation.
  useEffect(() => setText(value), [value]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the debounced text should trigger a commit.
  useEffect(() => {
    if (debounced.trim() !== value) onChange(debounced.trim());
  }, [debounced]);
  return (
    <SearchField
      aria-label={label}
      value={text}
      onChange={setText}
      onSubmit={(v) => onChange(v.trim())}
      onClear={() => onChange("")}
      className={className ?? "w-full"}
    >
      <SearchField.Group>
        <SearchField.SearchIcon />
        <SearchField.Input placeholder={placeholder} />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  );
}

/** Segmented control for a handful of mutually exclusive options, e.g. a period. */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: Array<{ id: T; label: string }>;
}) {
  return (
    <ToggleButtonGroup
      aria-label={label}
      selectionMode="single"
      disallowEmptySelection
      size="sm"
      selectedKeys={new Set<Key>([value])}
      onSelectionChange={(keys) => {
        const next = [...keys][0];
        if (next !== undefined) onChange(String(next) as T);
      }}
    >
      {options.map((o, i) => (
        <ToggleButton key={o.id} id={o.id}>
          {i > 0 ? <ToggleButtonGroup.Separator /> : null}
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
